import { useEffect, useRef, useState } from "react";
import useForm from "../../hooks/useForm";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
} from "../../reducers/buttonsReducer";
import { TokenCard, TokenCardRef } from "@almafintech/react-components";
import { getCompanyData, maskEmail, minutesToMiliseconds } from "../../utils";
import {
  onUpdateInitialValidationData,
  onUpdateLastCompletedSubStep,
  onUpdateStep,
  onUpdateDraftUUID,
  onUpdateSubStep,
} from "../../reducers/securitiesReducer";
import { FormikProps, FormikValues } from "formik";
import { tokenCard } from "../../i18n/inputs";
import { errors } from "../../i18n/securities/tokenStep";
import { authToken, regenerateToken } from "../../connectors/connectors";
import { updateToken } from "../../reducers/authReducer";
import { getDraftUUIDFromToken } from "../../app/utils";
import { handleGetDraft } from "../../utils/securities";

const TokenStep = ({
  formik,
  language,
}: {
  formik: FormikProps<FormikValues> | undefined;
  language: "es" | "en";
}) => {
  const tokenCardRef = useRef<TokenCardRef>(null);
  const { handleBack } = useForm(formik);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [lastTokenSent, setLastTokenSent] = useState<number>(Date.now());
  const [showInfoStep, setShowInfoStep] = useState(false);
  const state = useAppSelector((state) => state);
  const {
    initialValidationData: { email },
    accountHolders,
    currentHolder,
  } = state.securities;
  const { signatureRequestId } = state.authentication;
  const showHoldersMarriedStep = accountHolders === 2 && currentHolder === 1;

  const handleAuthorize = () => {
    if (tokenCardRef.current) {
      tokenCardRef?.current?.authorizeToken();
    }
  };

  useEffect(() => {
    dispatch(
      onUpdatePrimaryButton({
        onClick: handleAuthorize,
        text: tokenCard[language].primaryButton,
        type: "button",
        disabled: loading,
        isLoading: loading,
        show: true,
      })
    );
    dispatch(
      onUpdateSecondaryButton({
        text: tokenCard[language].secondaryButton,
        disabled: false,
        isLoading: false,
        onClick: handleBack,
        show: true,
      })
    );
  }, [loading]);

  return (
    <TokenCard
      ref={tokenCardRef}
      title={tokenCard[language].title}
      subtitle={tokenCard[language].subtitle}
      lastSendToken={lastTokenSent + minutesToMiliseconds(2)}
      resendText={tokenCard[language].resendText}
      incorrectTokenErrorMessage={
        tokenCard[language].incorrectTokenErrorMessage
      }
      lengthValidationErrorMessage={
        tokenCard[language].lengthValidationErrorMessage
      }
      onSuccess={async () => {
        setLoading(false);

        dispatch(
          onUpdateInitialValidationData({
            completed: true,
          })
        );
        if (showInfoStep) {
          dispatch(onUpdateSubStep(6 + (showHoldersMarriedStep ? 1 : 0)));
          dispatch(
            onUpdateLastCompletedSubStep(5 + (showHoldersMarriedStep ? 1 : 0))
          );
        } else {
          dispatch(onUpdateStep(1));
          dispatch(
            onUpdateLastCompletedSubStep(5 + (showHoldersMarriedStep ? 1 : 0))
          );
        }
      }}
      onAuthorize={async (token) => {
        setLoading(true);
        try {
          if (!signatureRequestId)
            throw new Error("Error: signatureRequestId not found");
          if (!formik) throw new Error("Error: formik not found");

          const authResponse = await authToken({
            email,
            signatureRequestId,
            token: token,
          });

          dispatch(updateToken(authResponse.data.token));

          const draftUUID = getDraftUUIDFromToken({
            token: authResponse.data.token,
          });

          dispatch(onUpdateDraftUUID(draftUUID));

          // Get draft from backend, parse steps data and update formik values
          await handleGetDraft({
            draftUUID,
            formik,
            dispatch,
            setShowInfoStep,
            state,
            language,
          });

          return {
            success: true,
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          setLoading(false);
          if (error.response?.data?.code === "INVALID_TOKEN") {
            return {
              success: false,
              message: errors.invalidToken[language],
            };
          } else if (error.response?.data?.code === "EXPIRED_TOKEN") {
            return {
              success: false,
              message: errors.expiredToken[language],
            };
          } else if (error.response?.data?.code === "MAX_ATTEMPTS_REACHED") {
            return {
              success: false,
              message: errors.maxAttempts[language],
            };
          } else
            return {
              success: false,
              message: errors.generic[language],
            };
        }
      }}
      onResend={async () => {
        if (!signatureRequestId || !email) return;

        try {
          await regenerateToken({
            email: email,
            signatureRequestId,
            companyType: getCompanyData().companyName,
          });

          setLastTokenSent(Date.now());
        } catch (error) {
          console.error(error);
        }
      }}
    >
      {tokenCard[language].resendText} {maskEmail(email)}
    </TokenCard>
  );
};

export default TokenStep;
